import React from "react";
import { Phone, Mail, MapPin, Clock, ShieldCheck, Facebook, Instagram } from "lucide-react";
import { Logo } from "./Logo";
import { useQuoteModal } from "@/hooks/useQuoteModal";

export const Footer: React.FC = () => {
  const { openModal } = useQuoteModal();
  return (
    <footer className="bg-slate-950 text-white border-t-4 border-amber-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          
          {/* Col 1: Brand Info */}
          <div className="space-y-4">
            <Logo variant="light" />
            <p className="text-slate-400 text-sm leading-relaxed mt-3">
              Davie, Florida's premier heavy lifting and local junk removal team. Family owned & operated — we do all the loading, hauling, and cleanouts so you don't have to!
            </p>

            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-slate-900 border border-slate-800 hover:border-amber-400 text-slate-300 hover:text-amber-400 flex items-center justify-center transition-colors"
                aria-label="Facebook Page"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-slate-900 border border-slate-800 hover:border-amber-400 text-slate-300 hover:text-amber-400 flex items-center justify-center transition-colors"
                aria-label="Instagram Page"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Col 2: Services Quick Links */}
          <div>
            <h3 className="text-amber-400 font-extrabold uppercase tracking-wider text-sm font-heading mb-4 pb-2 border-b border-slate-800">
              Junk Removal Services
            </h3>
            <ul className="space-y-2.5 text-sm text-slate-300">
              <li>
                <a href="#services" className="hover:text-amber-400 transition-colors">
                  Residential Junk Removal Davie
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-amber-400 transition-colors">
                  Commercial & Office Cleanout
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-amber-400 transition-colors">
                  Furniture & Couch Pickup
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-amber-400 transition-colors">
                  Appliance Hauling & Disposal
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-amber-400 transition-colors">
                  Garage, Attic & Basement Cleanouts
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-amber-400 transition-colors">
                  Yard Waste & Storm Debris
                </a>
              </li>
            </ul>
          </div>

          {/* Col 3: Service Areas */}
          <div>
            <h3 className="text-amber-400 font-extrabold uppercase tracking-wider text-sm font-heading mb-4 pb-2 border-b border-slate-800">
              Service Areas
            </h3>
            <ul className="space-y-2.5 text-sm text-slate-300">
              <li>Davie 33314 (East Davie / NSU)</li>
              <li>Davie 33324 (Pine Island Ridge)</li>
              <li>Davie 33328 (Shenandoah / Orange Dr)</li>
              <li>Davie 33330 (Forest Ridge / Griffin)</li>
              <li>Davie 33331 (Hawkes Bluff / Flamingo)</li>
              <li className="pt-1 text-xs text-amber-400/90 font-bold">
                Also serving Fort Lauderdale, Hollywood, Plantation, Cooper City & Sunrise!
              </li>
            </ul>
          </div>

          {/* Col 4: Contact Info */}
          <div>
            <h3 className="text-amber-400 font-extrabold uppercase tracking-wider text-sm font-heading mb-4 pb-2 border-b border-slate-800">
              Contact & Hours
            </h3>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="font-extrabold text-white">(954) 280-8090</div>
                  <div className="text-xs text-slate-400">Call or text anytime for instant quotes</div>
                </div>
              </li>

              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <a href="mailto:info@musclejunkremovalfl.com" className="hover:underline text-slate-200">
                  info@musclejunkremovalfl.com
                </a>
              </li>

              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span className="text-slate-300">
                  Davie, FL 33328 — Serving 30-Mile Radius Across South Florida
                </span>
              </li>

              <li className="flex items-start gap-2.5">
                <Clock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span>Monday – Sunday: 7:00 AM – 8:00 PM</span>
              </li>
            </ul>
          </div>

        </div>

        {/* SEO keyword paragraph */}
        <div className="mt-10 pt-8 border-t border-slate-900">
          <p className="text-xs text-slate-600 leading-relaxed text-center max-w-4xl mx-auto">
            Muscle Junk Removal provides professional junk removal services in Davie, FL and throughout Broward County. Whether you need junk removal in Fort Lauderdale, junk removal in Hollywood, junk removal in Plantation, junk removal in Cooper City, or junk removal in Sunrise, our local crew is ready to haul it away. We offer competitive junk removal prices with upfront, no-hidden-fee pricing. From furniture and appliance removal to full property cleanouts, we're South Florida's trusted junk hauling team.
          </p>
        </div>

        {/* Bottom copyright */}
        <div className="mt-8 pt-8 border-t border-slate-900 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
          <div>
            © {new Date().getFullYear()} Muscle Junk Removal Davie FL. All Rights Reserved.
          </div>
          <div className="flex gap-4">
            <a href="#services" className="hover:text-slate-300">Services</a>
            <span>•</span>
            <a href="#davie-area" className="hover:text-slate-300">Davie Service Area</a>
            <span>•</span>
            <button onClick={openModal} className="hover:text-slate-300">Free Quote</button>
            <span>•</span>
            <a href="#booking" className="hover:text-slate-300">Book Online</a>
          </div>
        </div>

      </div>
    </footer>
  );
};
